import styled from 'styled-components';
import { baseVariables } from '@marriott/mi-ui-library';

// Main wrapper - takes full available height
export const Wrapper = styled.div<{ $isSplitView?: boolean; $enablePageScroll?: boolean }>`
  background-color: var(--t-brand-bg-color, #ffffff);
  color: var(--t-brand-fg-color, #1c1c1c);
  display: flex;
  flex-direction: column;
  position: relative;
  width: 100%;
  border: none;

  ${props =>
    props.$isSplitView
      ? `
    /* Split view: vh/svh fallbacks; --o-chat-vvh from visualViewport fixes iOS shell stuck after keyboard */
    height: calc(100vh - 88px);
    height: calc(100svh - 88px);
    height: calc(var(--o-chat-vvh, 100svh) - 88px);
    max-height: calc(100vh - 88px);
    max-height: calc(100svh - 88px);
    max-height: calc(var(--o-chat-vvh, 100svh) - 88px);
    min-height: 0;
    padding: 0;
    align-items: stretch;
    justify-content: flex-start;
    overflow: hidden;
  `
      : `
    /* Welcome view - centered */
    align-items: center;
    justify-content: center;
    padding: 24px;

    @media ${baseVariables.mediaQuery.sm} {
      padding: 64px;
    }

  `}
  /* Mobile: Allow page scroll when enabled (Chat tab or Results list view) */
  /* Disable page scroll for Results map view */
  @media (max-width: 991px) {
    ${props =>
      props.$enablePageScroll !== false
        ? `
      height: auto;
      min-height: calc(100vh - 88px);
      min-height: calc(100svh - 88px);
      min-height: calc(var(--o-chat-vvh, 100svh) - 88px);
      max-height: none;
      overflow: visible;
    `
        : `
      height: calc(100vh - 88px);
      height: calc(100svh - 88px);
      height: calc(var(--o-chat-vvh, 100svh) - 88px);
      max-height: calc(100vh - 88px);
      max-height: calc(100svh - 88px);
      max-height: calc(var(--o-chat-vvh, 100svh) - 88px);
      overflow: hidden;
    `}
  }

  @media (max-width: 767px) {
    ${props =>
      props.$enablePageScroll !== false
        ? `
      min-height: calc(100vh - 51px);
      min-height: calc(100svh - 51px);
      min-height: calc(var(--o-chat-vvh, 100svh) - 51px);
    `
        : `
      height: calc(100vh - 51px);
      height: calc(100svh - 51px);
      height: calc(var(--o-chat-vvh, 100svh) - 51px);
      max-height: calc(100vh - 51px);
      max-height: calc(100svh - 51px);
      max-height: calc(var(--o-chat-vvh, 100svh) - 51px);
    `}
  }
`;

// Split layout container - column layout with input at bottom
export const SplitContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;
  height: 100%;
  overflow: hidden;
`;

// Content area - contains chat and preview sections side by side
export const ContentArea = styled.div<{ $enablePageScroll?: boolean }>`
  display: flex;
  flex: 1;
  min-height: 0;
  overflow: hidden;

  /* Mobile: Allow page scroll when enabled (Chat tab or Results list view) */
  /* Disable page scroll for Results map view */
  @media (max-width: 991px) {
    ${props =>
      props.$enablePageScroll !== false
        ? `
      /* Add padding for fixed chat header on mobile (Chat tab or Results list view) */
      padding-top: 48px;
      overflow: visible;
      min-height: auto;
      flex: none;
    `
        : `
      /* No padding in map view - map fills the viewport */
      padding-top: 0;
      overflow: hidden;
      min-height: 0;
      flex: 1;
    `}
  }

  /* Desktop: Restore internal scroll behavior */
  @media ${baseVariables.mediaQuery.lg} {
    overflow: hidden;
    min-height: 0;
    flex: 1;
    padding-top: 0;
  }
`;

// Chat section (left side in split view)
export const ChatSection = styled.div<{ $showChat?: boolean; $isNewChatMode?: boolean }>`
  display: flex;
  flex-direction: column;
  min-width: 0;
  height: 100%;
  background-color: var(--t-brand-bg-color, #ffffff);
  overflow: hidden;
  position: relative;
  overscroll-behavior: contain;

  /* Mobile defaults (< 768px) */
  flex: 1;
  border-right: none;
  display: ${props => (props.$showChat === false ? 'none' : 'flex')};

  /* Mobile: Allow page scroll instead of internal scroll */
  /* In new chat mode, use viewport height to allow flex-end to work */
  @media (max-width: 991px) {
    overflow: visible;
    ${props =>
      props.$isNewChatMode
        ? `
      /* Height = viewport - main header (52px) - content padding (48px) */
      height: calc(100vh - 100px);
      height: calc(100svh - 100px);
      min-height: calc(100vh - 100px);
      min-height: calc(100svh - 100px);
    `
        : `
      height: auto;
      min-height: 100%;
    `}

    /* Larger mobile viewports have taller main header (88px) */
    @media (min-width: 576px) {
      ${props =>
        props.$isNewChatMode
          ? `
        height: calc(100vh - 136px);
        height: calc(100svh - 136px);
        min-height: calc(100vh - 136px);
        min-height: calc(100svh - 136px);
      `
          : ''}
    }
  }

  /* Tablet/Desktop enhancements (768px and up) */
  @media ${baseVariables.mediaQuery.lg} {
    flex: 0 0 50%;
    border-right: 1px solid var(--colors-neutral-20, #eeeeee);
    display: flex; /* Always show on tablet/desktop */
    overflow: hidden;
    height: 100%;
  }
`;

// Preview section (right side in split view)
export const PreviewSection = styled.div<{ $showChat?: boolean; $enablePageScroll?: boolean }>`
  display: flex;
  flex-direction: column;
  flex: 1 1 0; /* Allow growing and shrinking, base size 0 */
  min-width: 0;
  height: 100%;
  overflow: auto;
  position: relative;

  /* Mobile defaults */
  display: ${props => (props.$showChat === false ? 'flex' : 'none')};

  /* Mobile: Use viewport height when Results tab is active and page scroll is enabled (list view) */
  @media (max-width: 991px) {
    ${props =>
      props.$showChat === false && props.$enablePageScroll !== false
        ? `
      min-height: calc(100vh - 100px);
      min-height: calc(100svh - 100px);
      height: auto;
    `
        : ''}
  }

  /* Tablet/Desktop enhancements (768px and up) */
  @media ${baseVariables.mediaQuery.lg} {
    display: flex; /* Always show on tablet/desktop */
  }
`;

export const ContentWrapper = styled.div<{ $isSplitView?: boolean }>`
  width: 100%;
  display: flex;
  justify-content: center;

  ${props =>
    props.$isSplitView &&
    `
    flex-direction: column;
    height: 100%;
  `}
`;

export const InnerContent = styled.div<{ $isSplitView?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  width: 100%;

  ${props =>
    props.$isSplitView
      ? `
    height: 100%;
    padding: 16px;
    max-width: none;
  `
      : `
    @media ${baseVariables.mediaQuery.sm} {
      max-width: 600px;
    }
  `}
`;

export const ImageWrapper = styled.div`
  width: 100px;
  height: 100px;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

export const TextBlockWrapper = styled.div<{ $isNewChatMode?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: ${props => (props.$isNewChatMode ? 'flex-start' : 'center')};
  width: 100%;
  border: none;

  @media ${baseVariables.mediaQuery.sm} {
    max-width: ${props => (props.$isNewChatMode ? 'none' : '425px')};
  }
`;

export const EyebrowWrapper = styled.div`
  margin-bottom: 8px;
  border: none;
`;

export const HeaderWrapper = styled.div<{ $isNewChatMode?: boolean }>`
  display: ${props => (props.$isNewChatMode ? 'none' : 'flex')};
  justify-content: center;
  padding-bottom: 8px;
  width: 100%;
  text-align: center;
  border: none;

  /* Override AHeader styles for Figma design */
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    font-family: var(--typography-title-font-family, 'Swiss721 BT', sans-serif);
    font-weight: var(--typography-title-l-font-weight, 700);
    line-height: 1.1;
    color: var(--t-brand-fg-color, #1c1c1c);
    border: none;
  }
`;

export const BodyWrapper = styled.div<{ $isNewChatMode?: boolean }>`
  width: 100%;
  border: none;

  /* Welcome (first visit): hide body on mobile; new chat: show at all breakpoints */
  ${props =>
    props.$isNewChatMode
      ? `
    display: flex;
    justify-content: flex-start;
    padding-bottom: 4px;
    text-align: left;
  `
      : `
    display: none;
    @media ${baseVariables.mediaQuery.lg} {
      display: flex;
      justify-content: center;
      padding-bottom: 4px;
      text-align: center;
    }
  `}

  /* Override ABody styles for Figma design */
  p,
  span {
    font-family: 'Swiss721 BT', sans-serif;
    font-size: 14px;
    line-height: 1.5;
    color: var(--t-brand-alt-color, #707070);
    border: none;
  }
`;

export const ChatFieldWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 4px; /* Space between input and disclaimer matching Figma design */
`;

export const InputWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background-color: var(--color-set-brand-bg-color, var(--t-brand-bg-color, #ffffff));
  border: 1px solid var(--color-set-horizontal-vertical-rule, var(--t-horizontal-vertical-rule, #c4c4c4));
  border-radius: var(--border-radius-cards-border-radius, 14px);
  box-shadow: 0px 4px 20px 0px rgba(0, 0, 0, 0.07);
  cursor: pointer;
  transition: border-color 0.2s ease;

  &:focus-within {
    border-color: var(--color-set-button-primary-border-color, var(--t-brand-fg-color, #1c1c1c));
    outline: none;
  }
`;

export const Input = styled.input`
  flex: 1;
  background: transparent;
  border: none !important;
  border-bottom: none !important;
  outline: none !important;
  font-family: 'Swiss721 BT', sans-serif;
  font-weight: 500 !important;
  font-size: 14px !important;
  height: 24px;
  line-height: 24px;
  padding: 0 !important;
  padding-bottom: 0 !important;
  margin: 0;
  color: var(--t-brand-fg-color, #1c1c1c);
  box-sizing: border-box;

  &::placeholder {
    color: var(--t-brand-alt-color, #707070);
  }

  &:focus {
    outline: none !important;
    border: none !important;
    border-bottom: none !important;
    box-shadow: none !important;
  }

  &:focus-visible {
    outline: none !important;
    box-shadow: none !important;
  }
`;

export const IconWrapper = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
  color: var(--t-brand-fg-color, #1c1c1c);

  &:focus {
    outline: 2px solid var(--t-brand-fg-color, #1c1c1c);
    outline-offset: 2px;
  }

  /* Icon size */
  [class^='icon-'],
  [class*=' icon-'] {
    font-size: 24px;
  }
`;

export const ChipGroupWrapper = styled.div<{ $isNewChatMode?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: ${props => (props.$isNewChatMode ? 'flex-start' : 'center')};
  justify-content: ${props => (props.$isNewChatMode ? 'flex-start' : 'center')};
  width: 100%;

  /* Desktop: chips come after chat input */
  @media ${baseVariables.mediaQuery.sm} {
    order: 2;
  }
`;

export const ChipMultifield = styled.div<{ $isNewChatMode?: boolean }>`
  display: flex;
  flex-wrap: wrap;
  align-content: center;
  align-items: center;
  justify-content: ${props => (props.$isNewChatMode ? 'flex-start' : 'center')};
  gap: 8px;
  width: 100%;
`;

export const ChipItem = styled.button`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  height: 32px;
  padding: 0 16px;
  background-color: var(--colors-neutral-10, #f4f4f4);
  border: 1px solid var(--colors-neutral-20, #eeeeee);
  border-radius: 99px;
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.2s ease;

  &:hover {
    background-color: var(--colors-neutral-20, #eeeeee);
    border-color: var(--colors-neutral-30, #d6d6d6);
  }

  &:focus {
    outline: 2px solid var(--t-brand-fg-color, #1c1c1c);
    outline-offset: 2px;
  }

  &:active {
    transform: scale(0.98);
  }
`;

export const ChipText = styled.span`
  font-family: 'Swiss721 BT', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.5;
  color: var(--t-brand-fg-color, #1c1c1c);
  white-space: nowrap;
  padding-bottom: 2px;
`;

// Messages container for chat history
export const MessagesContainer = styled.div<{ $isEmpty?: boolean; $isNewChatMode?: boolean }>`
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-gutter: stable; /* Reserve space for scrollbar to prevent layout shifts */
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
  min-height: 0; /* Critical for flex child to allow scrolling */
  ${props =>
    !(props.$isEmpty || props.$isNewChatMode) &&
    `
    padding-right: 0;
  `}

  /* Center content when empty (welcome screen) - but push to bottom in new chat mode */
  ${props =>
    props.$isEmpty &&
    !props.$isNewChatMode &&
    `
    justify-content: center;
    align-items: center;
  `}

  /* New chat mode - push content to bottom */
  ${props =>
    props.$isEmpty &&
    props.$isNewChatMode &&
    `
    justify-content: flex-end;
    align-items: flex-start;
    padding-bottom: 0;
  `}

  /* Mobile: Allow page scroll instead of internal scroll */
  /* But in new chat mode, keep flex: 1 to allow justify-content: flex-end to work */
  @media (max-width: 991px) {
    overflow-y: visible;
    overflow-x: visible;
    scrollbar-gutter: auto;
    ${props =>
      props.$isEmpty && props.$isNewChatMode
        ? `
      flex: 1;
      min-height: 0;
    `
        : `
      flex: none;
      min-height: auto;
    `}
  }

  /* Desktop: Restore internal scroll */
  @media ${baseVariables.mediaQuery.lg} {
    overflow-y: auto;
    flex: 1;
    min-height: 0;
  }

  &::-webkit-scrollbar {
    width: 0.375rem;
  }

  &::-webkit-scrollbar-track {
    background: var(--t-brand-bg-color, #ffffff);
  }

  &::-webkit-scrollbar-thumb {
    background: var(--t-horizontal-vertical-rule, #c4c4c4);
    border-radius: 0.1875rem;
  }
`;

// Welcome section wrapper (shown when no messages)
export const WelcomeSection = styled.div<{ $isNewChatMode?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: ${props => (props.$isNewChatMode ? 'flex-start' : 'center')};
  justify-content: center;
  gap: ${props => (props.$isNewChatMode ? '16px' : '24px')};
  width: 100%;
  max-width: ${props => (props.$isNewChatMode ? 'none' : '550px')};
  padding-bottom: ${props => (props.$isNewChatMode ? '1rem' : '0')};
`;

// Status indicator for loading states
export const StatusIndicatorWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: 8px 16px;
  font-size: 14px;
  color: var(--t-brand-alt-color, #707070);
  font-family: 'Swiss721 BT', sans-serif;
`;

// PreviewPanel wrapper for hotel results (full height in split view)
// Supports $hidden prop to keep component mounted but visually hidden for smooth transitions
export const PreviewPanelWrapper = styled.div<{ $hidden?: boolean }>`
  width: 100%;
  height: 100%;
  flex: 1 1 0;
  min-height: 0;
  overflow: auto; /* Changed from hidden to auto to allow list view scrolling */
  display: flex;
  flex-direction: column;

  /* Hidden state - keeps component mounted but invisible */
  ${({ $hidden }) =>
    $hidden &&
    `
    visibility: hidden;
    position: absolute;
    pointer-events: none;
  `}

  /* Ensure all child containers take full height */
  > * {
    height: 100%;
    flex: 1;
  }

  /* Ensure map fills the entire space */
  .map-container {
    height: 100% !important;
    min-height: 100% !important;
  }

  /* Override map-view min-height for chat context */
  .map-view {
    min-height: 100% !important;
    height: 100% !important;
    display: flex;
    flex-direction: column;
    overflow: hidden; /* Keep map view from scrolling */
  }

  /* Allow list view to scroll */
  .list-view {
    container: list-view / inline-size;
    overflow: auto;
  }

  /* Remove padding and box-shadow from search results container for map view */
  .search-results-container,
  .search-map-view-result-container {
    padding: 0 !important;
    box-shadow: none !important;
  }

  /* Override the fixed height on active-drawer */
  .search-map-view-result-container.active-drawer,
  .active-drawer {
    height: 100% !important;
  }

  /* Ensure the search result container takes full height */
  .m-container-fullbleed {
    padding: 0 !important;
    margin: 0 !important;
  }

  /* Ensure search form container takes full height */
  .search-form-container {
    height: 100% !important;
    flex: 1;
    display: flex;
    flex-direction: column;
    width: 100%;
    &::-webkit-scrollbar {
      display: none;
    }
    &::-webkit-scrollbar-track {
      display: none;
    }
    &::-webkit-scrollbar-thumb {
      display: none;
    }
    &::-webkit-scrollbar-thumb:hover {
      display: none;
    }
  }

  .filters-container {
    box-shadow: none !important;
  }

  .map-button-container.fixed-button {
    position: absolute !important;
    bottom: 24px;
    left: 50% !important;
    right: unset !important;
    transform: translateX(-50%) !important;
    top: unset !important;
    z-index: 102;

    @media (max-width: 767px) {
      bottom: env(safe-area-inset-bottom);
    }
  }
`;

// Map placeholder wrapper - fills entire preview section
// Supports $hidden prop to keep component mounted but visually hidden for smooth transitions
export const MapPlaceholder = styled.div<{ $hidden?: boolean }>`
  width: 100%;
  height: 100%;
  flex: 1;
  position: relative;
  overflow: hidden;
  background: var(--t-brand-bg-color, #ffffff);

  /* Hidden state - keeps component mounted but invisible */
  ${({ $hidden }) =>
    $hidden &&
    `
    visibility: hidden;
    position: absolute;
    pointer-events: none;
  `}

  /* Ensure MapComponent and its children fill the container */
  > div {
    width: 100%;
    height: 100%;
  }

  /* AImage picture element and img should fill container (fallback) */
  picture,
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

// Chat input wrapper - at bottom of chat section, full width within chat
export const ChatInputContainer = styled.div<{ $isWelcome?: boolean }>`
  width: 100%;
  padding: 0 1.5rem calc(0.5rem + env(safe-area-inset-bottom)) 1.5rem;
  background-color: var(--t-brand-bg-color, #ffffff);
  flex-shrink: 0;
  z-index: 0;

  /* Mobile: Make input sticky at bottom for page scroll */
  @media (max-width: 991px) {
    position: sticky;
    bottom: 0;
    z-index: 10;
  }

  /* On desktop welcome screen, hide this bottom input because it moves to the center */
  ${props =>
    props.$isWelcome &&
    `
    @media ${baseVariables.mediaQuery.lg} {
      display: none;
    }
  `}
`;

export const DesktopWelcomeInputWrapper = styled.div`
  width: 100%;
  display: none;
  max-width: 550px;
  @media ${baseVariables.mediaQuery.lg} {
    display: flex;
  }
`;

export const DesktopHeaderWrapper = styled.div`
  width: 100%;
  flex-shrink: 0;
  display: none;

  @media ${baseVariables.mediaQuery.lg} {
    display: block;
  }
`;

export const MobileHeaderWrapper = styled.div`
  width: 100%;
  flex-shrink: 0;
  background-color: var(--t-brand-bg-color, #ffffff);
  z-index: 48; /* Below main header (49) but above content */

  /* Mobile: Always use fixed positioning */
  position: fixed;
  left: 0;
  right: 0;
  top: 0;

  /* Initial position - below main header. JS will update this directly for instant response. */
  transform: translateY(52px);

  @media (min-width: 576px) {
    transform: translateY(88px);
  }

  /* Desktop: Hide */
  @media ${baseVariables.mediaQuery.lg} {
    display: none;
  }
`;

export const ScrollToBottomButton = styled.button<{ $isVisible: boolean }>`
  position: absolute;
  left: 50%;
  bottom: 6.25rem;
  bottom: calc(6.25rem + env(safe-area-inset-bottom));
  padding: 10px;
  border-radius: 50%;
  background: var(--t-brand-bg-color, #ffffff);
  color: var(--t-brand-fg-color, #1c1c1c);
  box-shadow: 0 0 30px 0 rgba(0, 0, 0, 0.25), 0 4px 4px 0 rgba(0, 0, 0, 0.1);
  z-index: 3;
  opacity: ${props => (props.$isVisible ? 1 : 0)};
  transform: ${props => (props.$isVisible ? 'translate(-50%, 0)' : 'translate(-50%, 8px)')};
  pointer-events: ${props => (props.$isVisible ? 'auto' : 'none')};
  transition: opacity 180ms ease, transform 100ms ease;

  /* Mobile: Use fixed positioning for page scroll context */
  @media (max-width: 991px) {
    position: fixed;
    bottom: calc(5rem + env(safe-area-inset-bottom));
    z-index: 11;
  }

  @media (max-width: 767px) {
    body:has([data-component-name='a-mi-chat-components-ATextArea'] textarea:focus) & {
      opacity: 0;
      pointer-events: none;
    }
  }
`;

// Main content area in split view (header on top, chat + preview side by side below)
export const SplitMainContent = styled.div<{ $enablePageScroll?: boolean }>`
  display: flex;
  flex-direction: column;
  flex: 1 1 0; /* Allow growing and shrinking, base size 0 */
  width: 100%;
  height: 100%;
  min-height: 0;
  max-height: 100%;
  overflow: hidden;

  /* Mobile: Allow page scroll when enabled (Chat tab or Results list view) */
  /* Disable page scroll for Results map view */
  @media (max-width: 991px) {
    ${props =>
      props.$enablePageScroll !== false
        ? `
      overflow: visible;
      height: auto;
      min-height: auto;
      max-height: none;
      flex: none;
    `
        : `
      overflow: hidden;
      height: 100%;
      min-height: 0;
      max-height: 100%;
      flex: 1 1 0;
    `}
  }

  /* Desktop: Restore internal scroll behavior */
  @media ${baseVariables.mediaQuery.lg} {
    overflow: hidden;
    height: 100%;
    min-height: 0;
    max-height: 100%;
    flex: 1 1 0;
  }
`;
